/**
 * AnalyticsProvider - Wraps the app with automatic analytics tracking
 * Includes: page views, session recording, automatic event capture
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  initAnalytics,
  identifyUser,
  resetAnalytics,
  trackPageView,
  trackClick,
} from '@/lib/analytics';

interface AnalyticsProviderProps {
  children: React.ReactNode;
  enableSessionRecording?: boolean;
  enableAutoClickTracking?: boolean;
}

// Session recording state
interface RecordingState {
  sessionId: string;
  events: unknown[];
  startedAt: Date;
  isRecording: boolean;
}

export function AnalyticsProvider({
  children,
  enableSessionRecording = true,
  enableAutoClickTracking = true,
}: AnalyticsProviderProps) {
  const location = useLocation();
  const { user } = useAuth();
  const recordingState = useRef<RecordingState | null>(null);
  const flushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rrwebStopRef = useRef<(() => void) | null>(null);

  // Initialize analytics on mount
  useEffect(() => {
    initAnalytics(user?.id);

    if (user?.id) {
      identifyUser(user.id);
    }

    return () => {
      resetAnalytics();
    };
  }, [user?.id]);

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Flush session recording events to Supabase
  const flushRecordingEvents = useCallback(async () => {
    if (!recordingState.current || recordingState.current.events.length === 0) {
      return;
    }

    const state = recordingState.current;
    const eventsToSend = [...state.events];
    state.events = []; // Clear the queue

    try {
      // Upsert the session recording (use type assertion - types not yet regenerated)
      const { error } = await (supabase as any)
        .from('session_recordings')
        .upsert({
          session_id: state.sessionId,
          user_id: user?.id || null,
          anonymous_id: localStorage.getItem('outlier_anonymous_id'),
          events: eventsToSend,
          started_at: state.startedAt.toISOString(),
          ended_at: new Date().toISOString(),
          duration_ms: Date.now() - state.startedAt.getTime(),
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight,
          event_count: eventsToSend.length,
        }, {
          onConflict: 'session_id',
        });

      if (error) {
        console.warn('[Analytics] Failed to save recording:', error.message);
        // Re-queue events on failure
        state.events.unshift(...eventsToSend);
      }
    } catch (err) {
      console.warn('[Analytics] Error saving recording:', err);
    }
  }, [user?.id]);

  // Initialize session recording with rrweb
  useEffect(() => {
    if (!enableSessionRecording) return;

    const initRecording = async () => {
      try {
        // Dynamically import rrweb to reduce initial bundle size
        const { record } = await import('rrweb');

        const sessionId = localStorage.getItem('outlier_session_id') || crypto.randomUUID();

        recordingState.current = {
          sessionId,
          events: [],
          startedAt: new Date(),
          isRecording: true,
        };

        // Start recording
        const stopFn = record({
          emit: (event) => {
            if (recordingState.current) {
              recordingState.current.events.push(event);

              // Flush when we have 100+ events
              if (recordingState.current.events.length >= 100) {
                flushRecordingEvents();
              }
            }
          },
          // Mask sensitive data for FINRA compliance
          maskAllInputs: true,
          maskTextSelector: '[data-mask]',
          blockSelector: '[data-block-recording]',
          // Sample mouse movements to reduce data size
          sampling: {
            mousemove: true,
            mouseInteraction: true,
            scroll: 150, // Sample scroll every 150ms
            input: 'last', // Only capture final input value
          },
        });

        if (stopFn) {
          rrwebStopRef.current = stopFn;
        }

        // Flush events every 30 seconds
        flushIntervalRef.current = setInterval(flushRecordingEvents, 30000);
      } catch (err) {
        console.warn('[Analytics] Failed to initialize session recording:', err);
      }
    };

    initRecording();

    return () => {
      if (rrwebStopRef.current) {
        rrwebStopRef.current();
      }
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
      }
      // Final flush on unmount
      flushRecordingEvents();
    };
  }, [enableSessionRecording, flushRecordingEvents]);

  // Auto-track clicks on buttons, links, and interactive elements
  useEffect(() => {
    if (!enableAutoClickTracking) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Find the closest interactive element
      const interactive = target.closest('button, a, [role="button"], [data-track]');

      if (interactive) {
        const elementName =
          interactive.getAttribute('data-track') ||
          interactive.getAttribute('aria-label') ||
          interactive.textContent?.slice(0, 50) ||
          interactive.tagName.toLowerCase();

        const properties: Record<string, unknown> = {
          tag: interactive.tagName.toLowerCase(),
          page: location.pathname,
        };

        // Add href for links
        if (interactive.tagName === 'A') {
          properties.href = (interactive as HTMLAnchorElement).href;
        }

        trackClick(elementName, properties);
      }
    };

    document.addEventListener('click', handleClick, { capture: true });

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [enableAutoClickTracking, location.pathname]);

  // Flush on page unload
  useEffect(() => {
    const handleUnload = () => {
      flushRecordingEvents();
    };

    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushRecordingEvents();
      }
    });

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [flushRecordingEvents]);

  return <>{children}</>;
}
